import { useState, useEffect } from 'react';
import toast from 'react-hot-toast';
import { API_BASE_URL } from '../utils/apiConfig';
import { supabase } from '../utils/supabase';
import NumberInput from './ui/NumberInput';

interface CancelPurchaseDialogProps {
    isOpen: boolean;
    purchase: {
        id: string;
        invoiceId?: string;
        totalAmount: number;
        paidAmount: number;
        lineItems?: any[];
    };
    onClose: () => void;
    onSuccess: () => void;
}

const CANCELLATION_REASONS = [
    'Member Request',
    'Error in Order',
    'Payment Failed',
    'Product Return',
    'Other'
];

export default function CancelPurchaseDialog({
    isOpen,
    purchase,
    onClose,
    onSuccess
}: CancelPurchaseDialogProps) {
    const [reason, setReason] = useState('');
    const [notes, setNotes] = useState('');
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [issueRefund, setIssueRefund] = useState(false);
    const [refundAmount, setRefundAmount] = useState<number>(0);

    // Update refund amount when paidAmount changes or dialog opens
    useEffect(() => {
        if (purchase.paidAmount) {
            setRefundAmount(Number(purchase.paidAmount));
        }
    }, [purchase.paidAmount, isOpen]);

    if (!isOpen) return null;

    const handleSubmit = async () => {
        if (!reason) {
            toast.error('Please select a cancellation reason');
            return;
        }

        if (issueRefund) {
            if (!notes) {
                toast.error('Additional notes are mandatory when issuing a refund');
                return;
            }
            if (refundAmount < 0) {
                toast.error('Refund amount cannot be negative');
                return;
            }
            if (refundAmount > (Number(purchase.paidAmount) || 0)) {
                toast.error(`Refund amount cannot exceed amount paid (₹${Number(purchase.paidAmount)})`);
                return;
            }
        }

        setIsSubmitting(true);
        try {
            const { data: { session } } = await supabase.auth.getSession();
            if (!session?.access_token) {
                throw new Error('Not authenticated');
            }

            const response = await fetch(
                `${API_BASE_URL}/api/gym-purchases/${purchase.id}`,
                {
                    method: 'DELETE',
                    headers: {
                        'Content-Type': 'application/json',
                        'Authorization': `Bearer ${session.access_token}`
                    },
                    body: JSON.stringify({
                        reason,
                        notes,
                        issueRefund,
                        refundAmount: issueRefund ? refundAmount : 0
                    })
                }
            );

            const data = await response.json();

            if (!response.ok) {
                throw new Error(data.error || 'Failed to cancel purchase');
            }

            toast.success(issueRefund ? 'Purchase refunded successfully' : 'Purchase cancelled successfully');
            onSuccess();
            handleClose();
        } catch (error: any) {
            console.error('Error cancelling purchase:', error);
            toast.error(error.message || 'Failed to cancel purchase');
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleClose = () => {
        setReason('');
        setNotes('');
        setIssueRefund(false);
        onClose();
    };

    return (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-[100] p-4">
            <div className="bg-white rounded-3xl shadow-2xl max-w-md w-full p-8 animate-in zoom-in duration-300">
                {/* Header */}
                <div className="flex items-center justify-between mb-8">
                    <div>
                        <h2 className="text-2xl font-black text-gray-900 tracking-tight">Cancel Purchase</h2>
                        <p className="text-xs text-gray-400 mt-1 uppercase tracking-widest font-bold">Void transaction & revoke access</p>
                    </div>
                    <button
                        onClick={handleClose}
                        disabled={isSubmitting}
                        className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-xl transition-all"
                    >
                        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                        </svg>
                    </button>
                </div>

                {/* Purchase Info Card */}
                <div className="bg-red-50 border border-red-100 rounded-2xl p-5 mb-8">
                    <div className="flex items-start gap-4">
                        <div className="w-10 h-10 rounded-xl bg-red-100 flex items-center justify-center text-red-600 shrink-0">
                            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                            </svg>
                        </div>
                        <div className="flex-1 min-w-0">
                            <p className="text-sm font-bold text-red-900 mb-1">Warning: Irreversible action</p>
                            <p className="text-xs text-red-700 leading-relaxed font-medium">
                                Cancelling <span className="font-black text-red-900">#{purchase.invoiceId || purchase.id.slice(0, 8)}</span> will void the transaction and revoke all associated plans and access immediately.
                            </p>
                        </div>
                    </div>
                </div>

                {/* Form Fields */}
                <div className="space-y-6">
                    <div>
                        <label className="block text-xs font-black text-gray-400 uppercase tracking-widest mb-2 ml-1">
                            Cancellation Reason <span className="text-red-500">*</span>
                        </label>
                        <select
                            value={reason}
                            onChange={(e) => setReason(e.target.value)}
                            disabled={isSubmitting}
                            className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-2xl focus:ring-4 focus:ring-blue-500/10 focus:border-blue-500 transition-all font-bold text-gray-700 text-sm outline-none appearance-none"
                            required
                        >
                            <option value="">Select a reason</option>
                            {CANCELLATION_REASONS.map((r) => (
                                <option key={r} value={r}>{r}</option>
                            ))}
                        </select>
                    </div>

                    {/* Refund Toggle */}
                    {Number(purchase.paidAmount) > 0 && (
                        <div className="bg-gray-50 rounded-2xl p-4 border border-gray-100">
                            <label className="flex items-center justify-between cursor-pointer group">
                                <div className="space-y-0.5">
                                    <span className="text-sm font-black text-gray-900">Issue Refund</span>
                                    <p className="text-[10px] text-gray-500 font-bold uppercase tracking-wider">Record reversal of payment</p>
                                </div>
                                <div className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none ${issueRefund ? 'bg-blue-600' : 'bg-gray-200'}`}>
                                    <input
                                        type="checkbox"
                                        className="sr-only"
                                        checked={issueRefund}
                                        onChange={(e) => setIssueRefund(e.target.checked)}
                                    />
                                    <span className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${issueRefund ? 'translate-x-5' : 'translate-x-0'}`} />
                                </div>
                            </label>

                            {issueRefund && (
                                <div className="mt-4 pt-4 border-t border-gray-200 space-y-4 animate-in slide-in-from-top-2 duration-300">
                                    <div>
                                        <div className="flex items-center justify-between mb-2">
                                            <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest">Refund Amount</label>
                                            <span className="text-[10px] font-black text-blue-600 bg-blue-50 px-2 py-0.5 rounded-lg">Max: ₹{Number(purchase.paidAmount).toLocaleString()}</span>
                                        </div>
                                        <div className="relative">
                                            <span className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-500 font-black">₹</span>
                                            <NumberInput
                                                min={0}
                                                emptyValue={0}
                                                value={refundAmount}
                                                onChange={(v) => setRefundAmount(v ?? 0)}
                                                placeholder="0.00"
                                                className="w-full pl-8 pr-4 py-3 bg-white border border-gray-200 rounded-2xl focus:ring-4 focus:ring-blue-500/10 focus:border-blue-500 transition-all font-black text-gray-900 outline-none"
                                            />
                                        </div>
                                    </div>
                                </div>
                            )}
                        </div>
                    )}

                    <div>
                        <label className="block text-xs font-black text-gray-400 uppercase tracking-widest mb-2 ml-1">
                            Additional Notes {issueRefund ? <span className="text-red-500">*</span> : '(Optional)'}
                        </label>
                        <textarea
                            value={notes}
                            onChange={(e) => setNotes(e.target.value)}
                            disabled={isSubmitting}
                            placeholder={issueRefund ? "Please provide details for the refund..." : "Enter any additional details..."}
                            rows={3}
                            className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-2xl focus:ring-4 focus:ring-blue-500/10 focus:border-blue-500 transition-all font-bold text-gray-700 text-sm outline-none resize-none"
                            required={issueRefund}
                        />
                    </div>

                    {/* Actions */}
                    <div className="flex gap-3 pt-2">
                        <button
                            onClick={handleClose}
                            disabled={isSubmitting}
                            className="flex-1 px-4 py-4 bg-gray-100 text-gray-600 rounded-2xl hover:bg-gray-200 font-black text-xs uppercase tracking-widest transition-all active:scale-95 disabled:opacity-50"
                        >
                            Return
                        </button>
                        <button
                            onClick={handleSubmit}
                            disabled={isSubmitting || !reason || (issueRefund && !notes)}
                            className={`flex-[2] px-4 py-4 ${issueRefund ? 'bg-blue-600 hover:bg-blue-700' : 'bg-red-600 hover:bg-red-700'} text-white rounded-2xl font-black text-xs uppercase tracking-widest transition-all active:scale-95 shadow-lg shadow-blue-500/20 disabled:opacity-50 flex items-center justify-center gap-2`}
                        >
                            {isSubmitting ? (
                                <>
                                    <svg className="animate-spin h-4 w-4" fill="none" viewBox="0 0 24 24">
                                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                    </svg>
                                    {issueRefund ? 'Processing...' : 'Voiding...'}
                                </>
                            ) : (
                                issueRefund ? 'Process Refund' : 'Void Purchase'
                            )}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
}

